import { Alert, Box, Card, CardContent, Chip, Divider, Stack, Tooltip, Typography } from '@mui/material';
import {
  BooleanInput, Edit, EditButton, Show, useRecordContext, SimpleForm, TextInput, TopToolbar,
  required, type RaRecord
} from 'react-admin';
import { BackLink } from './back';
import { OrderTrend } from './orderTrend';
import { Link } from 'react-router-dom';

type Organization = RaRecord & {
  name: string; acronym?: string | null; type?: string | null;
  ico?: string | null; dic?: string | null; icDph?: string | null; verified?: boolean | null;
  orderCount?: number;
  members?: { id: number; email: string; fullName: string | null; groupName: string | null; points: number; enabled: boolean }[];
  orders?: {
    id: number; ordered: string | null; customer: string | null; fulfilled: boolean;
    totalWithVat: number | null; via: 'organization' | 'member';
  }[];
  namedAfterMember?: boolean;
  monthly?: { month: string; orders: number }[];
  averageDeliveryDays?: number | null;
  deliveryMeasuredOn?: number;
};

const EUR = new Intl.NumberFormat('sk-SK', { style: 'currency', currency: 'EUR' });

function OrganizationBody() {
  const record = useRecordContext<Organization>();
  if (record == null) return null;

  const members = record.members ?? [];
  const orders = record.orders ?? [];

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6">{record.name}</Typography>
        {record.acronym != null && <Chip size="small" variant="outlined" label={record.acronym} />}
        {record.verified === true && <Chip size="small" color="success" variant="outlined" label="Overená" />}
      </Stack>

      <Stack direction="row" spacing={3}>
        {record.ico != null && <Typography variant="body2">IČO {record.ico}</Typography>}
        {record.dic != null && <Typography variant="body2">DIČ {record.dic}</Typography>}
        {record.icDph != null && <Typography variant="body2">IČ DPH {record.icDph}</Typography>}
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="overline" color="text.secondary">People ({members.length})</Typography>
          {members.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              K účtu tejto organizácie nie je priradený nikto.
            </Typography>
          ) : (
            <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
              {members.map(member => (
                <Stack key={member.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.75 }}>
                  <span>
                    <Link to={`/users/${member.id}/show`}>{member.fullName ?? member.email}</Link>
                    {member.fullName != null && (
                      <Typography variant="caption" display="block" color="text.secondary">{member.email}</Typography>
                    )}
                  </span>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {member.groupName != null && <Chip size="small" color="primary" variant="outlined" label={member.groupName} />}
                    <Typography variant="caption" color="text.secondary">{member.points} pts</Typography>
                    {!member.enabled && <Chip size="small" label="Neaktívny" />}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <OrderTrend
        monthly={record.monthly ?? []}
        averageDeliveryDays={record.averageDeliveryDays ?? null}
        measuredOn={record.deliveryMeasuredOn ?? 0}
      />

      <Card variant="outlined">
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Objednávky {record.orderCount != null && `(${record.orderCount})`}
          </Typography>
          {orders.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Žiadne objednávky.</Typography>
          ) : (
            <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
              {orders.map(order => (
                <Stack key={order.id} direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ py: 0.75 }}>
                  <Stack sx={{ minWidth: 0 }}>
                    <Link to={`/orders/${order.id}/show`}>#{order.id}</Link>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {order.ordered?.slice(0, 10) ?? '—'}
                      {order.customer != null && ` · objednal ${order.customer}`}
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    {order.totalWithVat != null && (
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {EUR.format(order.totalWithVat)}
                      </Typography>
                    )}
                    {/* Two ways an order lands here, and they mean different
                        things: booked to the company, or placed by one of its
                        people on their own account. */}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={order.via === 'organization' ? 'na organizáciu' : 'cez používateľa'}
                    />
                    {order.fulfilled
                      ? <Chip size="small" color="success" variant="outlined" label="Vybavená" />
                      : <Chip size="small" color="warning" variant="outlined" label="Otvorená" />}
                  </Stack>
                </Stack>
              ))}
            </Stack>
          )}
          {record.orderCount != null && record.orderCount > orders.length && (
            <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
              Zobrazených {orders.length} najnovších z {record.orderCount}.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Two different relationships, and they do not have to agree. */}
      <Alert severity="info" variant="outlined">
        Používatelia sú priradení cez spoločný účet. Objednávky sem patria dvoma spôsobmi: buď sú
        zaúčtované priamo na organizáciu, alebo ich zadal niekto z jej používateľov na vlastný účet —
        pri každej je uvedené, ktorý prípad to je. Organizácia preto môže mať objednávky bez používateľov
        aj naopak.
      </Alert>
    </Stack>
  );
}

export function OrganizationShow() {
  return (
    <Show
      title="Organizácia"
      actions={
        <TopToolbar sx={{ justifyContent: 'space-between' }}>
          <BackLink />
          <EditButton label="Upraviť údaje" />
        </TopToolbar>
      }
    >
      <OrganizationBody />
    </Show>
  );
}

/**
 * The organisation's own details.
 *
 * Limited to what describes the company - its name, short form, the three tax
 * identifiers and whether it has been verified. The slug is its address on the
 * storefront and the account is how its people are found; neither is a detail
 * anybody is trying to correct, and typing over either would break something
 * elsewhere, so neither is here.
 */
export function OrganizationEdit() {
  return (
    <Edit
      title="Organizácia"
      mutationMode="pessimistic"
      redirect="show"
      actions={<TopToolbar sx={{ justifyContent: 'flex-start' }}><BackLink /></TopToolbar>}
    >
      <SimpleForm>
        <TextInput source="name" label="Názov" validate={required()} fullWidth />
        <TextInput source="acronym" label="Skratka" />
        <Stack direction="row" spacing={2}>
          <TextInput source="ico" label="IČO" />
          <TextInput source="dic" label="DIČ" />
          <TextInput source="icDph" label="IČ DPH" />
        </Stack>
        <BooleanInput source="verified" label="Overená" />
      </SimpleForm>
    </Edit>
  );
}
